//@ts-expect-error not typed yet
import { provideThreadView } from 'conversations-thread-view/thread-view/decorators/provideThreadView';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { isFailed, isLoading } from 'conversations-async-data/async-data/operators/statusComparators';
import { LogLevel } from 'conversations-error-reporting/error-reporting/constants/LogLevel';
import { reportError } from 'conversations-error-reporting/error-reporting/reportError';
import { getCurrentInboxTeamIds, getCurrentInboxVisibility } from 'conversations-inbox-lib/unified-inbox/public/selectors';
import { USERS_AND_TEAMS } from 'conversations-internal-schema/inbox/constants/InboxVisibilityTypes';
import { useCurrentAssignee } from 'conversations-thread-data/assignees/public/hooks';
import { useAssignThread, useFetchUsersAndTeamsHierarchy } from 'conversations-thread-data/move-to-inbox/public/hooks';
import { getCrmConversationId, getOriginalGenericChannelId, getThreadId } from 'conversations-thread-data/thread-details/public/operators';
import NewFeatureTourStep from 'conversations-thread-view/new-feature-onboarding/components/NewFeatureTourStep';
import { REASSIGN } from 'conversations-thread-view/new-feature-onboarding/constants/OnboardingConstants';
import { useThreadDetails } from 'conversations-thread-view/thread-details-feature/public/hooks/useThreadDetails';
import { RotateIcon } from 'foundations-assets';
import { Text } from 'foundations-components/components/Text';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Small } from 'foundations-components/transitional/components/Small';
import promiseDone from 'hs-promise-utils/promiseDone';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { buildTeamsSet } from 'routing-ui/routing-rules/operators/buildTeamsSet';
import { filterUnroutableTeams } from 'routing-ui/routing-rules/operators/filterUnroutableTeams';
import toJS from 'transmute/toJS';
import GenericReassignmentDropdown from './GenericReassignmentDropdown';
import ReassignmentSelectInputPlaceholder from './ReassignmentSelectInputPlaceholder';
import { fetchResponder } from './clients/fetchResponder';
import { alertError } from './operators/alertError';
import { updateStateWithUpdateAssigneeFromTeamResponse } from './operators/updateStateWithUpdateAssigneeFromTeamResponse';

/*

  NOTE: the below is 3 separate components fused together to create 1

  The exported component `ReassignmentSelectDropdownContainer` wraps
  `ThreadViewReassignmentContainer` which is an instance of `ReassignmentContainer`
  passed through `provideThreadView`

  The reason this is all done is to confidently get data from the correct Redux store

  If `ReassignmentContainer` is wrapped with `provideThreadView`, the store created by
  `ThreadViewStoreSingleton` becomes the "nearest" store. As a result, when selectors are
  used by `ReassignmentContainer`, they are not accessing the Inbox store, but the Thread View
  store. This means slices that are expected, specifically the `unified-inbox` slice, are not
  present. It's simply by luck this has worked previously because once the Thread View store
  is created, this component would not be able to get the current inbox information it needs

*/
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ReassignmentContainer = ({
  inboxId,
  threadId: threadIdProp,
  visibleTeamIds
}) => {
  const {
    asyncThreadDetails
  } = useThreadDetails({
    threadId: threadIdProp
  });
  const threadDetails = getData(asyncThreadDetails) || undefined;
  const threadId = getThreadId(threadDetails);
  const conversationId = getCrmConversationId(threadDetails);
  const genericChannelId = getOriginalGenericChannelId(threadDetails);
  if (threadDetails && !inboxId) {
    const ERROR_NAME = 'UNDEFINED_INBOX_ID';
    reportError({
      // @ts-expect-error toJS here returns type "unknown"
      extra: toJS(threadDetails),
      fingerprint: ['{{ default }}', `${ERROR_NAME}`],
      level: LogLevel.info,
      tags: {
        isVisibleToUser: false
      }
    });
  }
  const {
    asyncAssignee,
    reassignThread,
    refetch: refetchAssignee,
    unassignThread
  } = useCurrentAssignee({
    conversationId: conversationId || 0,
    /*  @ts-expect-error Jun-7-2024, 14:44UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */
    threadId,
    skip: !conversationId
  });
  const currentAssignee = getData(asyncAssignee);
  const {
    data: hierarchyData,
    loading: hierarchyDataIsLoading,
    error: hierarchyError
  } = useFetchUsersAndTeamsHierarchy({
    inboxId: inboxId || 0,
    skip: !inboxId
  });
  const eligibleTeams = useMemo(() => {
    const teams = hierarchyData ? hierarchyData.usersAndTeamsHierarchy : [];
    return filterUnroutableTeams(teams, visibleTeamIds || buildTeamsSet(teams));
  }, [hierarchyData, visibleTeamIds]);
  const [routeToTeam, {
    loading: reassignmentIsLoading
  }] = useAssignThread({
    onCompleted: response => {
      // CT-12743: This is a specific fix for this issue
      // We are directly writing to the cache as a short/medium-term fix to prevent flakiness
      // There is an existing issue where gql returns null for the current assignee
      // because the cv crm object is not updated fast enough
      if (threadId && response.assignThread['@type'] === 'ASSIGNED') {
        const agentId = response.assignThread.assignedActor.split('-')[1];
        promiseDone(fetchResponder(agentId).then(responder => {
          updateStateWithUpdateAssigneeFromTeamResponse(responder, threadId);
        }).catch(error => {
          alertError({
            error,
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "conversations-inbox-ui.reassignment.ownerError.message"
            }),
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "conversations-inbox-ui.reassignment.ownerError.title"
            })
          });
        }));
      }
    }
  });

  // TODO: Remove calling and definition of useRefetchAssignee together
  // useRefetchAssignee(refetchAssignee, currentAssignee, threadDetails);

  const loading = hierarchyDataIsLoading || reassignmentIsLoading || isLoading(asyncAssignee);
  const error = hierarchyError || isFailed(asyncAssignee);
  if (error) {
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Small, {
        use: "error",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-inbox-ui.reassignment.unableToLoadAssignee"
        })
      }), /*#__PURE__*/_jsxs(IconButton, {
        use: "link",
        size: "small",
        placement: "right",
        onClick: () => {
          refetchAssignee().catch(refetchError => {
            reportError({
              error: refetchError
            });
          });
        },
        marginInlineStart: "200",
        children: [/*#__PURE__*/_jsx(RotateIcon, {}), /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-inbox-ui.reassignment.retryLoadAssignees"
          })
        })]
      })]
    });
  }
  const renderSelectDropdown = () => {
    if (loading) {
      return /*#__PURE__*/_jsx(ReassignmentSelectInputPlaceholder, {});
    }

    // The explicit casting replaced previous implicit casting, and will be removed in a future clean-up
    return /*#__PURE__*/_jsx(GenericReassignmentDropdown, {
      currentAssignee: currentAssignee,
      genericChannelId: genericChannelId,
      inboxId: inboxId,
      reassignThread: reassignThread,
      routeToTeam: routeToTeam,
      teams: eligibleTeams,
      threadId: threadIdProp,
      threadDetails: threadDetails,
      unassignThread: unassignThread
    });
  };
  return /*#__PURE__*/_jsx(NewFeatureTourStep, {
    stepKey: REASSIGN,
    header: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "conversations-inbox-ui.new-feature-onboarding.reassign.header"
    }),
    body: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "conversations-inbox-ui.new-feature-onboarding.reassign.body"
    }),
    placement: "bottom left",
    children: /*#__PURE__*/_jsxs(Text, {
      typography: "body-100",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "heading-50",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-inbox-ui.reassignment.ownerText"
        })
      }), renderSelectDropdown()]
    })
  });
};
ReassignmentContainer.displayName = 'ReassignmentContainer';
const ReassignmentDropdownWithThreadView = provideThreadView(ReassignmentContainer);
const ReassignmentSelectDropdownContainer = props => {
  const inboxTeamIds = useSelector(getCurrentInboxTeamIds);
  const inboxVisibility = useSelector(getCurrentInboxVisibility);
  const visibleTeamIds = useMemo(() => inboxVisibility === USERS_AND_TEAMS ? new Set(inboxTeamIds.toArray()) : undefined, [inboxTeamIds, inboxVisibility]);
  return /*#__PURE__*/_jsx(ReassignmentDropdownWithThreadView, Object.assign({
    visibleTeamIds: visibleTeamIds
  }, props));
};
ReassignmentSelectDropdownContainer.displayName = 'ReassignmentSelectDropdownContainer';
export default ReassignmentSelectDropdownContainer;